import { api } from './api';

// ---------- houses ----------
export interface House { id: string; name: string; colorHex: string; motto: string | null; isActive: boolean; members: number; points: number; }
/**
 * The scoreboard for ONE session.
 *
 * ⚠ `leaderIds` AND `isTie` COME FROM THE SERVER AND ARE NOT RECOMPUTED HERE. Two houses level on
 * 340 points are joint first, and a client that takes the first row of a sorted array shows one of
 * them winning — at an assembly, in front of both.
 */
export interface HouseBoard {
  academicYear: string;
  isAllTime: boolean;
  seasons: string[];
  houses: House[];
  leaderIds: string[];
  isTie: boolean;
}

export interface HousePointRow {
  points: number; source: string; reason: string;
  studentName: string | null; on: string; academicYear: string;
}

export const housesApi = {
  /** `year` omitted = this session. `'all'` = the honours-board total across every year. */
  /** `institutionId` omitted = the trust-wide roll-up. A campus board passes one. */
  leaderboard: (year?: string, institutionId?: string) => {
    const q = new URLSearchParams();
    if (year) q.set('year', year);
    if (institutionId) q.set('institutionId', institutionId);
    return api.get<HouseBoard>(`/api/houses${q.toString() ? `?${q}` : ''}`);
  },
  create: (b: { name: string; institutionId: string; colorHex: string; motto: string | null }) => api.post<House>('/api/houses', b),
  update: (id: string, b: { name: string; colorHex: string; motto: string | null; isActive: boolean }) => api.put<House>(`/api/houses/${id}`, b),
  /** ⚠ Retires, never destroys — the ledger, the posts and every past appointment reference it. */
  retire: (id: string) => api.del<{ retired: boolean }>(`/api/houses/${id}`),
  assign: (studentId: string, houseId: string | null) => api.post('/api/houses/assign', { studentId, houseId }),
  award: (b: { houseId: string; points: number; source: string; reason: string; studentId: string | null; academicYear?: string | null }) => api.post('/api/houses/award', b),
  ledger: (id: string, year?: string) => api.get<HousePointRow[]>(`/api/houses/${id}/ledger${year ? `?year=${encodeURIComponent(year)}` : ''}`),

  /** ⚠ Writes nothing. Returns exactly what applying WOULD do. */
  /** ⚠ institutionId is REQUIRED — a trust-wide allocation is not a thing anybody means. */
  allocationPlan: (institutionId: string, includeAssigned = false) =>
    api.get<AllocationPlan>(`/api/houses/allocation/plan?institutionId=${institutionId}&includeAssigned=${includeAssigned}`),
  /** ⚠ Re-plans server-side rather than trusting the plan the browser is holding. */
  allocationApply: (institutionId: string, includeAssigned = false) =>
    api.post<AllocationPlan>(`/api/houses/allocation/apply?institutionId=${institutionId}&includeAssigned=${includeAssigned}`, {}),
};

export interface AllocationRow {
  studentId: string; fullName: string; admissionNumber: string;
  classLabel: string | null; houseId: string; houseName: string; because: string | null;
}
export interface HouseTally { houseId: string; houseName: string; colorHex: string; members: number; }
export interface AllocationPlan {
  moves: AllocationRow[];
  alreadyAssigned: number;
  unplaceable: number;
  before: HouseTally[];
  after: HouseTally[];
  notes: string[];
}

// ---------- sports ----------
export interface SportsItem { id: string; name: string; category: string | null; quantity: number; notes: string | null; }
export interface SportResult { position: number; label: string | null; houseId: string | null; studentName: string | null; housePoints: number; }
export interface SportEvent { id: string; name: string; sport: string | null; date: string; isInterHouse: boolean; resultsRecorded: boolean; results: SportResult[]; }
export const sportsApi = {
  items: () => api.get<SportsItem[]>('/api/sports/items'),
  addItem: (b: { institutionId: string; name: string; category: string | null; quantity: number; notes: string | null }) => api.post<SportsItem>('/api/sports/items', b),
  restock: (id: string, by: number) => api.post<SportsItem>(`/api/sports/items/${id}/restock`, { by }),
  deleteItem: (id: string) => api.del<void>(`/api/sports/items/${id}`),
  events: () => api.get<SportEvent[]>('/api/sports/events'),
  createEvent: (b: { institutionId: string; name: string; sport: string | null; date: string; isInterHouse: boolean }) => api.post<SportEvent>('/api/sports/events', b),
  recordResult: (b: { eventId: string; position: number; label: string | null; houseId: string | null; studentId: string | null; studentName: string | null; housePoints: number }) => api.post<SportEvent>('/api/sports/events/result', b),
};

// ---------- leadership ----------
export interface Post { id: string; title: string; category: string | null; houseId: string | null; rank: number; isActive: boolean; }
export interface Appointment { id: string; postTitle: string; studentId: string; studentName: string; houseId: string | null; academicYear: string; viaElection: boolean; startDate: string; endDate: string | null; isActive: boolean; }
export interface Candidate { id: string; studentId: string; studentName: string; manifesto: string | null; votes: number; }
export interface Election { id: string; postId: string; postTitle: string; academicYear: string; status: string; electorate: string; electorateRefId: string | null; winnerCandidateId: string | null; totalVotes: number; candidates: Candidate[]; }

export const leadershipApi = {
  posts: () => api.get<Post[]>('/api/leadership/posts'),
  createPost: (b: { title: string; institutionId: string; category: string | null; houseId: string | null; rank: number }) => api.post<Post>('/api/leadership/posts', b),
  appointments: () => api.get<Appointment[]>('/api/leadership/appointments'),
  appoint: (b: { postId: string; studentId: string; studentName: string; houseId: string | null; academicYear: string; startDate: string; endDate: string | null }) => api.post<Appointment>('/api/leadership/appointments', b),
  endAppointment: (id: string) => api.post(`/api/leadership/appointments/${id}/end`, {}),
  elections: () => api.get<Election[]>('/api/leadership/elections'),
  createElection: (b: { postId: string; academicYear: string; electorate: string; electorateRefId: string | null }) => api.post<Election>('/api/leadership/elections', b),
  addCandidate: (b: { electionId: string; studentId: string; studentName: string; manifesto: string | null }) => api.post<Election>('/api/leadership/elections/candidate', b),
  open: (id: string) => api.post<Election>(`/api/leadership/elections/${id}/open`, {}),
  close: (id: string) => api.post<Election>(`/api/leadership/elections/${id}/close`, {}),
  declare: (id: string, startDate: string, endDate: string | null) => api.post<Appointment>(`/api/leadership/elections/${id}/declare`, { startDate, endDate }),
  vote: (electionId: string, candidateId: string) => api.post('/api/elections/vote', { electionId, candidateId }),
  forStudent: (studentId: string) => api.get<Appointment[]>(`/api/students/${studentId}/appointments`),
};
